import { ACTION } from "@/lib/auth/actions";
import { useAuthV2, type Staff } from "@/lib/authv2/store";
import { hasPermission } from "@/lib/authv2/permissions";
import { deptWriteDoorClosed } from "@/lib/auth/dept-door";
import { opsRole } from "@/lib/auth/page-access";

type GalleryUser = Pick<Staff, "role" | "department"> | null | undefined;

// photo_idphoto: GNCA photographers carry this code (backend gallery/handler.go
// keeps the same list — change both together).
//
// makeup + marketing: quyết định 0219 (CEO chốt 26/09/2026) — hai phòng làm
// được mọi việc như thợ ảnh. Lượt đó chỉ sửa danh sách ở máy chủ, nên máy chủ
// trả cây album (chi tiết đơn hiện nút "Mở Gallery") mà màn này vẫn báo "không
// có quyền". Phạm vi đơn (trang điểm: đơn có tên mình · marketing: mọi đơn)
// nằm ở máy chủ, không ở đây.
const READ_DEPARTMENTS = new Set(["photo", "photo_idphoto", "photo_profile", "photo_corporate", "retouch", "cskh", "makeup", "marketing"]);
const ASSET_WRITE_DEPARTMENTS = new Set(["photo", "photo_idphoto", "photo_profile", "photo_corporate", "retouch", "makeup", "marketing"]);
const ALBUM_WRITE_DEPARTMENTS = READ_DEPARTMENTS;

function department(user: GalleryUser): string {
  return user?.department?.trim().toLowerCase() ?? "";
}

export function canReadGallery(user: GalleryUser): boolean {
  return !!user && (hasPermission(ACTION.GalleryRead) || READ_DEPARTMENTS.has(department(user)));
}

export function canWriteGalleryAssets(user: GalleryUser): boolean {
  return (
    !!user &&
    (hasPermission(ACTION.GalleryAssetWrite) || ASSET_WRITE_DEPARTMENTS.has(department(user)))
  );
}

export function canWriteGalleryAlbums(user: GalleryUser): boolean {
  return (
    !!user &&
    (hasPermission(ACTION.GalleryAlbumWrite) || ALBUM_WRITE_DEPARTMENTS.has(department(user)))
  );
}

export function canWriteGalleryComments(user: GalleryUser): boolean {
  return (
    !!user && (hasPermission(ACTION.GalleryCommentWrite) || READ_DEPARTMENTS.has(department(user)))
  );
}

/**
 * Tải ảnh về máy (tệp ZIP, tải gốc): gương của `galleryActor` ở máy chủ
 * (backend/internal/core/gallery/handler.go — `CanDownload`) — đổi một bên phải
 * đổi bên kia. Có gallery.asset.write, hoặc phòng đọc được kho ảnh (bốn phòng
 * ảnh, hậu kỳ, CSKH, trang điểm, marketing), hoặc sale.
 *
 * Đây là bản soi để khoá nút cho đúng, không phải chốt bảo mật: máy chủ còn
 * cắt theo cơ sở, theo ê-kíp và theo từng album.
 */
const SALES_DEPARTMENTS = new Set(["sale", "sales"]);

export function canDownloadGallery(user: GalleryUser): boolean {
  if (!user) return false;
  if (hasPermission(ACTION.GalleryAssetWrite) || READ_DEPARTMENTS.has(department(user))) return true;
  return user.role?.trim().toLowerCase() === "sale" || SALES_DEPARTMENTS.has(department(user));
}

/**
 * Vì sao vai này KHÔNG xin được tệp ZIP ở album đang mở — null là xin được.
 *
 * Sale có quyền tải nhưng cổng tệp ZIP của album ĐƠN không theo lối "đơn mình
 * bán" như cửa xem ảnh (máy chủ trả 403) — tải từng tấm ở khung chi tiết vẫn
 * được. `finalOnly` vốn đã false ở album ngoài đơn (sale vào đó qua lối album
 * ngoài đơn, máy chủ cho tải). Luật vai bên dưới áp cho CẢ album ngoài đơn.
 */
export function lyDoKhongTaiZip(user: GalleryUser, opts: { finalOnly: boolean }): string | null {
  if (opts.finalOnly) return "Sale tải từng tấm ở khung chi tiết";
  // Xin tệp ZIP là lệnh GHI: người vào kho ảnh nhờ PHÒNG mà mang vai chỉ-xem
  // thì cửa phòng ban đóng (galleryDeptDoorOpen — QC vẫn mở).
  const role = user?.role?.trim().toLowerCase() ?? "";
  const quaCuaPhong = !hasPermission(ACTION.GalleryRead) && READ_DEPARTMENTS.has(department(user));
  if (!canDownloadGallery(user) || (quaCuaPhong && role !== "qc" && deptWriteDoorClosed(role))) {
    return "Vai của bạn không tải ảnh về máy được";
  }
  return null;
}

/** Nút bị khoá phải NÓI lý do ngay trên nút, không ẩn đi và không để bấm rồi
 *  ăn 403 (luật "máy chủ từ chối thì phải nói lý do"). Một chỗ, ba nút. */
export const LY_DO_KHONG_CHON_HO = "Chỉ phòng chăm sóc khách hàng chọn hộ khách được";

/**
 * Chọn ảnh + chốt hộ khách: CHỈ phòng CSKH (máy chủ: gallery/handler.go nhóm
 * `selection` — đổi một bên phải đổi bên kia).
 *
 * 🔴 KHÔNG mượn canWriteGalleryAssets: hàm đó còn gác nút tải ảnh và gắn nhãn
 * của phòng chụp. Mượn là hoặc phòng chụp mất chỗ tải ảnh, hoặc phòng hậu kỳ
 * giữ lại quyền chốt số ảnh tính công của chính họ.
 *
 * Chặn theo PHÒNG chứ không nới theo vai: vai "manager" dùng chung cho trưởng
 * phòng mọi phòng, nới theo vai là trưởng phòng hậu kỳ lọt.
 */
export function canWriteGallerySelection(user: GalleryUser): boolean {
  return !!user && (hasPermission(ACTION.GallerySelectionWrite) || department(user) === "cskh");
}

/**
 * Nút "Mở lại ảnh gốc" (QĐ 0459, CEO chốt 3a 27/10/2026): Tổng giám đốc, Giám đốc,
 * Trưởng cửa hàng qua quyền; CSKH qua phòng ban. Gương `auth.AllowOrDept` ở
 * gallery/original_reopen.go — vai chỉ-xem / QC mang phòng CSKH bị đóng cửa ghi.
 */
export function canReopenOriginals(user: GalleryUser): boolean {
  if (!user) return false;
  if (hasPermission(ACTION.GalleryOriginalsReopen)) return true;
  return department(user) === "cskh" && !deptWriteDoorClosed(user.role);
}

/**
 * Khối "Ảnh khách gửi lại" (QĐ 0457): người chọn ảnh hộ khách cộng Trưởng cửa hàng
 * (CEO chốt 2b, 27/10/2026); Trưởng ca theo chức danh đi cùng đường (`opsRole`, gương
 * `auth.ActsAsStoreLead`). Máy chủ: gallery/handler.go `allowResubmitMatch`.
 */
export function canMatchResubmissions(
  user: (Pick<Staff, "role" | "department"> & { jobTitleCode?: string | null }) | null | undefined,
): boolean {
  return canWriteGallerySelection(user) || (!!user && opsRole(user) === "branch_manager");
}

/**
 * Gửi link album cho khách (tạo · xem · thu hồi link): CSKH và bốn mã phòng
 * ảnh. Máy chủ: gallery/handler.go `galleryShareWriteDepartments` — đổi một bên
 * phải đổi bên kia.
 *
 * Thợ chụp thêm vào 27/09/2026 (CEO chốt câu 2b, quyết định 0222): nút "Gửi
 * khuyến nghị cho khách" ở khung soi cạnh nhau là của chính thợ chụp, như bản
 * xem thử CEO duyệt. Trang điểm, marketing và hậu kỳ KHÔNG có — câu 2b chỉ thêm
 * một phòng được gửi link cho khách.
 */
const SHARE_DEPARTMENTS = new Set(["cskh", "photo", "photo_idphoto", "photo_profile", "photo_corporate"]);

export function canShareGallery(user: GalleryUser): boolean {
  return (
    !!user && (hasPermission(ACTION.GalleryShareWrite) || SHARE_DEPARTMENTS.has(department(user)))
  );
}

/**
 * "Mở lại chọn ảnh" — gương của cửa máy chủ (`galleryAllowOrDept(ActionSelectionUnlock,
 * galleryAssetWriteDepartments…)`), danh sách đó CÓ "cskh" (CEO chốt 9a 16/09/2026:
 * khách nhờ CSKH mở lại là chuyện thường ngày). Trước đây màn chỉ đọc
 * ASSET_WRITE_DEPARTMENTS (không có "cskh") nên CSKH thấy mục mờ dù máy chủ cho.
 */
export function canUnlockGallerySelection(user: GalleryUser): boolean {
  return (
    !!user &&
    (hasPermission(ACTION.GallerySelectionUnlock) ||
      ASSET_WRITE_DEPARTMENTS.has(department(user)) ||
      department(user) === "cskh")
  );
}

/**
 * Bỏ ảnh vượt gói hộ khách (QĐ 0466) — gương của rào máy chủ
 * (`galleryDropExtraDepartments`): quyền mở khoá danh sách (cấp quản lý) hoặc
 * phòng CSKH. Hẹp hơn "Mở lại chọn ảnh" CỐ Ý: cửa này làm giảm tiền khách trả,
 * thợ không được tự bỏ ảnh khách đã mua.
 */
export function canDropGalleryExtra(user: GalleryUser): boolean {
  return !!user && (hasPermission(ACTION.GallerySelectionUnlock) || department(user) === "cskh");
}

/** Lý do khoá mục "Bỏ ảnh vượt gói" với người không được bỏ hộ khách. */
export const LY_DO_KHONG_BO_ANH_VUOT = "Chỉ phòng chăm sóc khách hàng bỏ ảnh vượt gói hộ khách được";

export function canManageGallerySettings(user: GalleryUser): boolean {
  return user?.role?.trim().toLowerCase() === "admin" && hasPermission(ACTION.GallerySettingsWrite);
}

/**
 * Chuyển màu nước ở album Final (QĐ 0233). Máy chủ: watercolor/handler.go
 * `runDepartments` — đổi một bên phải đổi bên kia. Phòng Sale đi bằng quyền
 * (vai sale), phòng chụp KHÔNG có. Rào đơn/nhánh thật nằm ở máy chủ.
 */
const WATERCOLOR_DEPARTMENTS = new Set(["retouch", "cskh"]);

export function canRunWatercolor(user: GalleryUser): boolean {
  return (
    !!user && (hasPermission(ACTION.GalleryWatercolorRun) || WATERCOLOR_DEPARTMENTS.has(department(user)))
  );
}

/**
 * Xưởng ghép (QĐ 0233 câu 5b): cùng tập người với màu nước — hậu kỳ, CSKH,
 * sale và các vai quản lý. Thợ chụp / trang điểm / marketing KHÔNG có dù họ
 * tải được ảnh lên kho.
 */
export function canComposeFinal(user: GalleryUser): boolean {
  return (
    !!user && (hasPermission(ACTION.GalleryComposeWrite) || WATERCOLOR_DEPARTMENTS.has(department(user)))
  );
}

/**
 * Sale: không đọc được Kho ảnh nói chung, chỉ nhánh Final của đơn MÌNH BÁN
 * (máy chủ lọc cây và chặn từng ảnh — final_tools_access.go).
 */
export function isFinalOnlyGalleryViewer(user: GalleryUser): boolean {
  return !!user && !canReadGallery(user) && hasPermission(ACTION.GalleryComposeWrite);
}

/**
 * Bản PHẢN ỨNG của isFinalOnlyGalleryViewer cho thành phần hiển thị: đọc quyền
 * qua selector của kho trạng thái nên vẽ lại khi phiên đổi.
 */
export function useIsFinalOnlyGalleryViewer(): boolean {
  const staff = useAuthV2((s) => s.currentStaff);
  // Selector trả boolean, đọc `permissions` qua biến có thể rỗng: bản giả kho
  // trạng thái trong nhiều bài kiểm không có mục này (cùng lý do useDataVisibility).
  const granted = useAuthV2((s) => (s.permissions as Set<string> | undefined) ?? EMPTY);
  if (!staff) return false;
  const canRead = granted.has(ACTION.GalleryRead) || READ_DEPARTMENTS.has(department(staff));
  return !canRead && granted.has(ACTION.GalleryComposeWrite);
}

const EMPTY: ReadonlySet<string> = new Set<string>();

/** Sổ chi phí vẽ màu nước — chỉ ban giám đốc. */
export function canReadWatercolorCosts(user: GalleryUser): boolean {
  return !!user && hasPermission(ACTION.GalleryWatercolorCostRead);
}

/**
 * Trang Quản lý album (/albums, QĐ 0301): ai tới được cửa xem cây album thì mở
 * được trang. Gương của cổng tuyến ở máy chủ —
 * `allowOrFinalTools(ActionRead, galleryReadDepartments…)` trong
 * gallery/handler.go — đổi một bên phải đổi bên kia. Sale vào bằng
 * gallery.compose.write và chỉ thấy đơn mình bán (máy chủ lọc).
 */
export function canOpenAlbumManager(user: GalleryUser): boolean {
  return !!user && (canReadGallery(user) || hasPermission(ACTION.GalleryComposeWrite));
}

/**
 * Nút "Tạo album ngoài đơn" (QĐ 0306, L10): thợ chụp, marketing, sale theo
 * PHÒNG; trưởng cửa hàng, giám đốc, quản trị theo quyền gallery.loose.create.
 * Gương của `auth.AllowOrDept(ActionLooseCreate, looseCreateDepartments…)` ở
 * gallery/loose_handler.go — đổi một bên phải đổi bên kia.
 */
const LOOSE_CREATE_DEPARTMENTS = new Set(["photo", "photo_idphoto", "photo_profile", "photo_corporate", "marketing", "sale", "sales"]);
export function canCreateLooseAlbum(user: GalleryUser): boolean {
  return !!user && (hasPermission(ACTION.GalleryLooseCreate) || LOOSE_CREATE_DEPARTMENTS.has(department(user)));
}

/** Tạo album cho "toàn công ty" (không thuộc cơ sở nào): marketing, giám đốc, quản trị (máy chủ: loose.go). */
export function canCreateCompanyWideLooseAlbum(user: GalleryUser): boolean {
  const role = user?.role?.trim().toLowerCase();
  return !!user && (role === "admin" || role === "director" || department(user) === "marketing");
}

/** Sổ album ngoài đơn (/albums/ledger): quản trị, giám đốc (quyền gallery.loose.audit). */
export function canAuditLooseAlbums(user: GalleryUser): boolean {
  return !!user && hasPermission(ACTION.GalleryLooseAudit);
}

/**
 * Công tắc "Của tôi" (chỉ album tôi đã tải ảnh) mặc định BẬT với NHÂN VIÊN của
 * phòng có tải ảnh — bốn mã phòng ảnh, hậu kỳ, trang điểm, marketing — và TẮT
 * với quản lý, CSKH, sale: bật sẵn cho người không bao giờ tải ảnh là mở trang
 * ra thấy trống.
 */
export function albumManagerMineByDefault(user: GalleryUser): boolean {
  return user?.role?.trim().toLowerCase() === "staff" && ASSET_WRITE_DEPARTMENTS.has(department(user));
}
